import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';
import { useState, useEffect } from 'react';
import { publicApi } from '../lib/api';

const DEFAULT_VERSION = '5.9.0';

// Hook to fetch app version
function useAppVersion() {
  const [version, setVersion] = useState(DEFAULT_VERSION);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const response = await publicApi.getAppVersion();
        if (response.data?.version) {
          setVersion(response.data.version);
        }
        if (response.data?.updated_at) {
          setUpdatedAt(response.data.updated_at);
        }
      } catch (error) {
        // Use default version on error
      }
    };
    fetchVersion();
  }, []);

  return { version, updatedAt };
}

// Format date for display
function formatVersionDate(dateStr) {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return '';
  }
}

// Returns true if the release was published in the last 7 days
function isRecentRelease(dateStr) {
  if (!dateStr) return false;
  try {
    const released = new Date(dateStr).getTime();
    const ageDays = (Date.now() - released) / (1000 * 60 * 60 * 24);
    return ageDays >= 0 && ageDays <= 7;
  } catch {
    return false;
  }
}

export function Footer({ className = '' }) {
  const { version, updatedAt } = useAppVersion();
  return (
    <footer className={`py-8 border-t border-border ${className}`}>
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
          {/* Product */}
          <div>
            <h4 className="font-semibold mb-4">Produit</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><a href="/#fonctionnalites" className="hover:text-foreground transition-colors">Fonctionnalités</a></li>
              <li><a href="/#tarifs" className="hover:text-foreground transition-colors">Tarifs</a></li>
              <li><a href="/#demo" className="hover:text-foreground transition-colors">Démo</a></li>
              <li><Link to="/changelog" className="hover:text-foreground transition-colors">Changelog</Link></li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="font-semibold mb-4">Support</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/support" className="hover:text-foreground transition-colors">Centre d'aide</Link></li>
              <li><Link to="/contact" className="hover:text-foreground transition-colors">Contact</Link></li>
              <li><a href="mailto:support@resawork.com" className="hover:text-foreground transition-colors">support@resawork.com</a></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-semibold mb-4">Légal</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/legal/mentions" className="hover:text-foreground transition-colors">Mentions légales</Link></li>
              <li><Link to="/legal/privacy" className="hover:text-foreground transition-colors">Politique de confidentialité</Link></li>
              <li><Link to="/legal/cgu" className="hover:text-foreground transition-colors">CGU</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-semibold mb-4">Entreprise</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/about" className="hover:text-foreground transition-colors">À propos</Link></li>
              <li><a href="https://agence-msc.fr" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">Agence MSC</a></li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-6 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <img 
              src="/logo.png" 
              alt="ResaWork" 
              className="h-8"
            />
            <Link
              to="/changelog"
              className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center gap-2 group"
              title="Voir l'historique des versions"
            >
              <span className="font-medium">v{version}</span>
              {updatedAt && (
                <span className="text-xs opacity-60 group-hover:opacity-90">
                  ({formatVersionDate(updatedAt)})
                </span>
              )}
              {isRecentRelease(updatedAt) && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-semibold animate-pulse">
                  ✨ Nouveau
                </span>
              )}
            </Link>
          </div>
          <p className="text-sm text-muted-foreground text-center">
            © {new Date().getFullYear()} ResaWork. Solution développée par{' '}
            <a href="https://agence-msc.fr" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
              Agence MSC
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}

export function FooterMinimal({ showAdminLink = false }) {
  const { version, updatedAt } = useAppVersion();

  return (
    <div className="text-xs text-muted-foreground text-center py-2">
      <div className="flex items-center justify-center gap-2 flex-wrap">
        <Link to="/changelog" className="hover:text-primary transition-colors flex items-center gap-1.5">
          ResaWork v{version}
          {isRecentRelease(updatedAt) && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-primary/15 text-primary text-[9px] font-bold uppercase tracking-wide animate-pulse">
              ✨ New
            </span>
          )}
        </Link>
        {updatedAt && (
          <span className="opacity-50 text-[10px]">
            ({formatVersionDate(updatedAt)})
          </span>
        )}
        {showAdminLink && (
          <Link 
            to="/admin-login" 
            className="opacity-30 hover:opacity-100 transition-opacity"
            title="Administration"
          >
            <Shield className="w-3 h-3" />
          </Link>
        )}
      </div>
      <p className="mt-1 opacity-70">
        Par <a href="https://agence-msc.fr" target="_blank" rel="noopener noreferrer" className="hover:text-primary">Agence MSC</a>
      </p>
    </div>
  );
}

// Footer branded for client login pages - links to ResaWork website
export function FooterBranded() {
  const publicUrl = process.env.REACT_APP_PUBLIC_URL || window.location.origin;
  
  return (
    <div className="text-center py-3">
      <a 
        href={publicUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors group"
        title="Visitez ResaWork"
      >
        <span>Solution de réservation par</span>
        <img 
          src="/logo.png" 
          alt="ResaWork" 
          className="h-6 group-hover:scale-105 transition-transform"
        />
      </a>
    </div>
  );
}
